import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faXmark, faUserCircle, faUsers, 
  faKitMedical, faBicycle, faChild, faPersonBiking, 
  faMedal, faIdCard, faInfoCircle, faSpinner, faHashtag, faShirt
} from '@fortawesome/free-solid-svg-icons';
import { minorService } from '../services/database';
import { getCurrentUser } from '../utils/auth';

const AddMinor: React.FC = () => {
  const navigate = useNavigate();
  const [riderCategory, setRiderCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = () => {
    navigate('/profile');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Get the current logged-in user
      const currentUser = getCurrentUser();
      if (!currentUser) {
        setError('You must be logged in to add a minor.');
        return;
      }

      // Get form data
      const formData = new FormData(e.target as HTMLFormElement);
      
      // Validate ID Number
      const idNumber = formData.get('idNumber') as string;
      if (!idNumber || idNumber.trim() === '') {
        setError('ID number is required.');
        setLoading(false);
        return;
      }
      
      if (idNumber.length !== 13 || !/^\d{13}$/.test(idNumber)) {
        setError('ID number must be exactly 13 digits.');
        setLoading(false);
        return;
      }
      
      // Create minor object with all fields including emergency contact
      const minorData = {
        user_id: currentUser.id,
        name: formData.get('fullName') as string,
        date_of_birth: formData.get('dob') as string,
        gender: formData.get('gender') as string,
        id_number: idNumber || null, // Allow null for backward compatibility
        tshirt_size: (formData.get('tshirtSize') as string) || null,
        rider_category: riderCategory || 'beginner',
        emergency_contact_name: formData.get('guardianName') as string,
        emergency_contact_phone: formData.get('guardianPhone') as string,
        emergency_contact_relationship: formData.get('relationship') as string,
        allergies: formData.get('allergies') as string || 'None',
        medical_conditions: formData.get('medicalConditions') as string || 'None',
        csa_licence: formData.get('csaLicence') as string || undefined
      };

      console.log('Creating minor with data:', minorData);

      // Save to database
      await minorService.createMinor(minorData);
      
      console.log('Minor created successfully');
      
      // Navigate back to profile
      navigate('/profile');
      
    } catch (err) {
      console.error('Error creating minor:', err);
      setError('Failed to add minor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 font-sans text-dark">
      {/* Mobile Header */}
      <header className="app-header">
        <div className="flex items-center justify-between">
          <Link to="/profile" className="app-header-back">
            <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
          </Link>
          <h1 className="app-header-title">Add Minor</h1>
          <div className="w-6"></div> {/* Empty div for spacing */}
        </div>
      </header>

      <main className="content-container pt-16 pb-24">
        <div className="mb-6 mt-4">
          <p className="text-gray-600 text-sm">Please provide the details for the minor participant (under 18 years) you wish to register for mountain bike events.</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form id="minor-form" onSubmit={handleSubmit}>
          <div className="mb-6">
            <h2 className="text-md font-semibold mb-4 flex items-center">
              <FontAwesomeIcon icon={faUserCircle} className="text-primary mr-2" />
              Personal Information
            </h2>
            
            <div className="mb-4">
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">Full Name*</label>
              <input 
                type="text" 
                id="fullName" 
                name="fullName" 
                placeholder="Enter minor's full name" 
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                required
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1">Date of Birth*</label>
              <input 
                type="date" 
                id="dob" 
                name="dob" 
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender*</label>
              <div className="flex space-x-4">
                <div className="flex items-center">
                  <input 
                    type="radio" 
                    id="male" 
                    name="gender" 
                    value="male" 
                    className="h-4 w-4 text-primary" 
                    required
                  />
                  <label htmlFor="male" className="ml-2 text-sm text-gray-700">Male</label>
                </div>
                <div className="flex items-center">
                  <input 
                    type="radio" 
                    id="female" 
                    name="gender" 
                    value="female" 
                    className="h-4 w-4 text-primary" 
                  />
                  <label htmlFor="female" className="ml-2 text-sm text-gray-700">Female</label>
                </div>
                <div className="flex items-center">
                  <input 
                    type="radio" 
                    id="other" 
                    name="gender" 
                    value="other" 
                    className="h-4 w-4 text-primary" 
                  />
                  <label htmlFor="other" className="ml-2 text-sm text-gray-700">Other</label>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="idNumber" className="block text-sm font-medium text-gray-700 mb-1">ID Number*</label>
              <div className="flex items-center">
                <span className="mr-2 text-gray-500">
                  <FontAwesomeIcon icon={faHashtag} />
                </span>
                <input 
                  type="text" 
                  id="idNumber" 
                  name="idNumber" 
                  placeholder="Enter 13-digit ID number" 
                  maxLength={13}
                  pattern="[0-9]*"
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                  onInput={(e) => {
                    // Only allow numbers
                    const target = e.target as HTMLInputElement;
                    target.value = target.value.replace(/[^0-9]/g, '');
                  }}
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="tshirtSize" className="block text-sm font-medium text-gray-700 mb-1">T-Shirt Size</label>
              <div className="flex items-center">
                <span className="mr-2 text-gray-500">
                  <FontAwesomeIcon icon={faShirt} />
                </span>
                <select 
                  id="tshirtSize" 
                  name="tshirtSize" 
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  defaultValue=""
                >
                  <option value="">Select size (optional)</option>
                  <option value="XS">XS - Extra Small</option>
                  <option value="S">S - Small</option>
                  <option value="M">M - Medium</option>
                  <option value="L">L - Large</option>
                  <option value="XL">XL - Extra Large</option>
                  <option value="XXL">XXL - 2X Large</option>
                  <option value="XXXL">XXXL - 3X Large</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="csaLicence" className="block text-sm font-medium text-gray-700 mb-1">CSA Licence</label>
              <div className="flex items-center">
                <span className="mr-2 text-gray-500">
                  <FontAwesomeIcon icon={faIdCard} />
                </span>
                <input 
                  type="text" 
                  id="csaLicence" 
                  name="csaLicence" 
                  placeholder="Enter CSA Licence number (if available)" 
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-md font-semibold mb-4 flex items-center">
              <FontAwesomeIcon icon={faUsers} className="text-primary mr-2" />
              Guardian/Emergency Contact
            </h2>
            
            <div className="mb-4">
              <label htmlFor="guardianName" className="block text-sm font-medium text-gray-700 mb-1">Guardian Name*</label>
              <input 
                type="text" 
                id="guardianName" 
                name="guardianName" 
                placeholder="Enter guardian's full name" 
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                required
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="guardianPhone" className="block text-sm font-medium text-gray-700 mb-1">Emergency Phone Number*</label>
              <input 
                type="tel" 
                id="guardianPhone" 
                name="guardianPhone" 
                placeholder="Enter emergency contact number" 
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" 
                required
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="relationship" className="block text-sm font-medium text-gray-700 mb-1">Relationship to Minor*</label>
              <select 
                id="relationship" 
                name="relationship" 
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
                defaultValue=""
              >
                <option value="" disabled>Select relationship</option>
                <option value="parent">Parent</option>
                <option value="legal_guardian">Legal Guardian</option>
                <option value="relative">Relative</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-md font-semibold mb-4 flex items-center">
              <FontAwesomeIcon icon={faKitMedical} className="text-primary mr-2" />
              Medical Information
            </h2>
            
            <div className="mb-4">
              <label htmlFor="allergies" className="block text-sm font-medium text-gray-700 mb-1">Allergies</label>
              <textarea 
                id="allergies" 
                name="allergies" 
                placeholder="List any allergies (if none, write 'None')" 
                rows={2} 
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              ></textarea>
            </div>
            
            <div className="mb-4">
              <label htmlFor="medicalConditions" className="block text-sm font-medium text-gray-700 mb-1">Medical Conditions</label>
              <textarea 
                id="medicalConditions" 
                name="medicalConditions" 
                placeholder="List any medical conditions (if none, write 'None')" 
                rows={2} 
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              ></textarea>
            </div>
            
            <div className="mb-4">
              <label htmlFor="medications" className="block text-sm font-medium text-gray-700 mb-1">Current Medications</label>
              <textarea 
                id="medications" 
                name="medications" 
                placeholder="List any current medications (if none, write 'None')" 
                rows={2} 
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              ></textarea>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-md font-semibold mb-4 flex items-center">
              <FontAwesomeIcon icon={faBicycle} className="text-primary mr-2" />
              Rider Information
            </h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Rider Category*</label>
              <div className="grid grid-cols-3 gap-2">
                <div 
                  className={`border rounded-lg p-3 flex flex-col items-center cursor-pointer ${
                    riderCategory === 'beginner' ? 'border-primary bg-orange-50' : 'hover:border-primary'
                  }`}
                  onClick={() => setRiderCategory('beginner')}
                >
                  <input 
                    type="radio" 
                    id="beginner" 
                    name="riderCategory" 
                    value="beginner" 
                    className="sr-only" 
                    checked={riderCategory === 'beginner'}
                    onChange={() => setRiderCategory('beginner')}
                    required
                  />
                  <label htmlFor="beginner" className="cursor-pointer text-center">
                    <FontAwesomeIcon icon={faChild} className="text-2xl mb-2 text-gray-600" />
                    <span className="block text-sm">Beginner</span>
                  </label>
                </div>
                <div 
                  className={`border rounded-lg p-3 flex flex-col items-center cursor-pointer ${
                    riderCategory === 'intermediate' ? 'border-primary bg-orange-50' : 'hover:border-primary'
                  }`}
                  onClick={() => setRiderCategory('intermediate')}
                >
                  <input 
                    type="radio" 
                    id="intermediate" 
                    name="riderCategory" 
                    value="intermediate" 
                    className="sr-only" 
                    checked={riderCategory === 'intermediate'}
                    onChange={() => setRiderCategory('intermediate')}
                  />
                  <label htmlFor="intermediate" className="cursor-pointer text-center">
                    <FontAwesomeIcon icon={faPersonBiking} className="text-2xl mb-2 text-gray-600" />
                    <span className="block text-sm">Intermediate</span>
                  </label>
                </div>
                <div 
                  className={`border rounded-lg p-3 flex flex-col items-center cursor-pointer ${
                    riderCategory === 'expert' ? 'border-primary bg-orange-50' : 'hover:border-primary'
                  }`}
                  onClick={() => setRiderCategory('expert')}
                >
                  <input 
                    type="radio" 
                    id="expert" 
                    name="riderCategory" 
                    value="expert" 
                    className="sr-only" 
                    checked={riderCategory === 'expert'}
                    onChange={() => setRiderCategory('expert')}
                  />
                  <label htmlFor="expert" className="cursor-pointer text-center">
                    <FontAwesomeIcon icon={faMedal} className="text-2xl mb-2 text-gray-600" />
                    <span className="block text-sm">Expert</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="bikeType" className="block text-sm font-medium text-gray-700 mb-1">Bike Type</label>
              <select 
                id="bikeType" 
                name="bikeType" 
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                defaultValue=""
              >
                <option value="" disabled>Select bike type</option>
                <option value="mtb">Mountain Bike</option>
                <option value="bmx">BMX</option>
                <option value="hybrid">Hybrid</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input type="checkbox" name="helmetConfirmation" className="h-4 w-4 text-primary rounded" required />
                <span className="ml-2 text-sm text-gray-700">I confirm the minor will wear a helmet during all events</span>
              </label>
            </div>
          </div>

          <div className="mb-8 p-4 bg-gray-100 rounded-lg">
            <h2 className="text-md font-semibold mb-2">Parental Consent</h2>
            <p className="text-sm text-gray-700 mb-3">By registering this minor, I confirm that:</p>
            <ul className="text-sm text-gray-700 list-disc pl-5 mb-3">
              <li>I am the legal guardian or have permission from the legal guardian</li>
              <li>I understand the risks associated with mountain biking</li>
              <li>I give consent for emergency medical treatment if needed</li>
            </ul>
            <div className="flex items-center">
              <input type="checkbox" id="consentCheck" name="consentCheck" className="h-4 w-4 text-primary rounded" required />
              <label htmlFor="consentCheck" className="ml-2 text-sm font-medium text-gray-700">I agree to the above statements*</label>
            </div>
          </div>
        </form>
      </main>

      <footer className="app-bottom-nav px-4 py-3">
        <div className="flex justify-between">
          <button 
            type="button" 
            className="px-5 py-3 border border-gray-500 rounded-lg font-medium text-gray-300 hover:bg-gray-700 transition-colors"
            onClick={() => navigate('/profile')}
          >
            Save as Draft
          </button>
          <button 
            type="submit" 
            form="minor-form" 
            className="px-5 py-3 bg-primary text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default AddMinor;
